import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import {
  Specialization_DifficultyLevel as DifficultyLevel,
  CareerRole_PathwayLevel as PathwayLevel,
} from '__generated__/graphql-types';

import CareerRolesByDomainQuery from 'bundles/unified-common/queries/CareerRolesByDomain.graphql';
import type {
  CareerRolesByDomainFragment,
  CareerRolesByDomainQueryQuery as CareerRolesByDomainQueryType,
  CareerRolesByDomainQueryQueryVariables as CareerRolesByDomainQueryVariables,
} from 'bundles/unified-common/queries/__generated__/CareerRolesByDomain';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';

export const getPathwayLevel = (difficultyLevel?: DifficultyLevel) => {
  switch (difficultyLevel) {
    case DifficultyLevel.Beginner:
      return PathwayLevel.Beginner;
    case DifficultyLevel.Intermediate:
      return PathwayLevel.Intermediate;
    case DifficultyLevel.Advanced:
      return PathwayLevel.Advanced;
    default:
      return undefined;
  }
};

/**
 * @description Used to normalize the career roles by domain by adding the proficiency level to each pathway
 */
export const normalizeCareerRolesByDomain = (
  content?: CareerRolesByDomainFragment
): CareerRolesCollectionType | undefined => {
  if (!content) {
    return undefined;
  }

  const entities = content?.entities?.elements
    .map((entity) => {
      if (!entity || entity.__typename !== 'Skill_CareerRole') {
        return null;
      }

      const pathways = entity?.pathways
        ? {
            id: entity.pathways.id!,
            recommenderConfigurationHash: entity.pathways.recommenderConfigurationHash!,
            context: entity.pathways.context!,
            collectionTrackingId: entity.pathways.collectionTrackingId!,
            entities: entity.pathways.entities?.elements
              .map((pathway) => {
                if (
                  pathway?.__typename !== 'ProductCard_ProductCard' ||
                  pathway?.productTypeAttributes?.__typename !== 'ProductCard_Specialization'
                ) {
                  return null;
                }

                const product = pathway.productTypeAttributes.canonical;
                if (!product) return null;

                return {
                  ...product,
                  proficiencyLevel: getPathwayLevel(product.difficultyLevel),
                };
              })
              .filter(Boolean),
          }
        : undefined;

      return {
        id: entity.id,
        name: entity.name,
        shortDescription: entity.shortDescription,
        roleCardImage: entity.roleCardImage,
        merchandisingSkillNames: entity.merchandisingSkillNames,
        slug: entity.slug,
        domain: entity.domain,
        jobPostingData: entity.jobPostingData,
        skills: entity.skills,
        pathways,
      };
    })
    .filter(Boolean);

  return {
    id: content.id,
    additionalInput: content.additionalInput,
    context: content.context,
    recommenderConfigurationHash: content.recommenderConfigurationHash,
    entities,
  };
};

/**
 * @description Used to get the list of career roles for the given domain
 */
function useCareerRolesByDomain({
  ssr,
  skip,
  domainId,
  onCompleted,
}: {
  ssr: boolean;
  skip: boolean;
  domainId: string;
  onCompleted?: (data: CareerRolesByDomainQueryType) => void;
}) {
  const { data, loading } = useQuery<CareerRolesByDomainQueryType, CareerRolesByDomainQueryVariables>(
    CareerRolesByDomainQuery,
    {
      variables: {
        domainId,
      },
      notifyOnNetworkStatusChange: true,
      ssr,
      skip,
      context: { clientName: 'gatewayGql' },
      onCompleted,
      onError: (e) =>
        Sentry.captureException(e, {
          extra: {
            message: `Error fetching career roles by domain with domain ID "${domainId}"`,
          },
        }),
    }
  );

  return {
    data: normalizeCareerRolesByDomain(data?.PathwaySkillCareerRoles?.queryRolesByDomain ?? undefined),
    loading,
  };
}

export default useCareerRolesByDomain;
